import { useState } from 'react'
import { Check, ChevronDown, Gauge, Sparkles } from 'lucide-react'
import { Button } from '@/components/shadcn/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from '@/components/shadcn/dropdown-menu'

export const MODELS = [
  { id: 'claude-opus-5-5', label: 'Opus 5.5', note: 'Most capable' },
  { id: 'claude-fable-5-1', label: 'Fable 5.1', note: 'Long, careful work' },
  { id: 'claude-sonnet-5-5', label: 'Sonnet 5.5', note: 'Fast and capable' },
  { id: 'claude-haiku-5-5', label: 'Haiku 5.5', note: 'Fastest' },
] as const
export const EFFORTS = ['low', 'medium', 'high', 'xhigh', 'max'] as const

/** Last picked, per project: a new session starts on the box's default, so this is what was asked of this one. */
function useRemembered(key: string, fallback: string) {
  const [v, setV] = useState(() => localStorage.getItem(key) ?? fallback)
  return [v, (n: string) => { setV(n); localStorage.setItem(key, n) }] as const
}

/**
 * Model and effort beside Send. Each change is Claude Code's own `/model` or
 * `/effort` sent into the session, so the terminal and the chat agree.
 */
export function ModelPicker({ project, disabled, onCommand }: { project: string; disabled: boolean; onCommand: (text: string) => void }) {
  const [model, setModel] = useRemembered(`chat-model:${project}`, MODELS[0].id)
  // Low unless changed: the session's settings start Claude at low too. A new key, so an old remembered 'high' does not linger.
  const [effort, setEffort] = useRemembered(`chat-effort2:${project}`, 'low')
  const current = MODELS.find((m) => m.id === model) ?? MODELS[0]
  const trigger = 'h-7 gap-1 rounded-full px-2 text-[11px] font-normal text-fg-subtle'
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className={trigger} disabled={disabled} aria-label="Model"><Sparkles className="size-3" />{current.label}<ChevronDown className="size-3 opacity-60" /></Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" side="top" className="w-56">
          <DropdownMenuLabel className="text-xs">Model</DropdownMenuLabel>
          {MODELS.map((m) => (
            <DropdownMenuItem key={m.id} className="gap-2 text-xs" onSelect={() => { setModel(m.id); onCommand(`/model ${m.id}`) }}>
              <Check className={m.id === current.id ? 'size-3.5' : 'size-3.5 opacity-0'} />
              <span className="flex-1">{m.label}</span><span className="text-muted-foreground">{m.note}</span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className={trigger} disabled={disabled} aria-label="Effort"><Gauge className="size-3" />{effort}<ChevronDown className="size-3 opacity-60" /></Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" side="top" className="w-44">
          <DropdownMenuLabel className="text-xs">Effort</DropdownMenuLabel>
          {EFFORTS.map((e) => (
            <DropdownMenuItem key={e} className="gap-2 text-xs" onSelect={() => { setEffort(e); onCommand(`/effort ${e}`) }}>
              <Check className={e === effort ? 'size-3.5' : 'size-3.5 opacity-0'} />{e}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  )
}
